import type { Branded } from './branded'

export type Fail<E extends Error> = Branded<[E, undefined], 'attempt_fail'>
export type Success<T> = Branded<[undefined, T], 'attempt_success'>
export type Result<E extends Error, T> = Fail<E> | Success<T>
export type Attempt<E extends Error, T> = Promise<Result<E, T>>

export async function attempt<T>(asyncFunctionOrPromise: Promise<T> | (() => Promise<T>)): Attempt<Error, T>
export async function attempt<E extends Error>(asyncFunctionOrPromise: Promise<unknown> | (() => Promise<unknown>)): Attempt<E, unknown>
export async function attempt<E extends Error, T = any>(asyncFunctionOrPromise: Promise<T> | (() => Promise<T>)): Attempt<E, T>
export function attempt<T>(syncFunction: () => T): Result<Error, T>
export function attempt<E extends Error>(syncFunction: () => unknown): Result<E, unknown>
export function attempt<E extends Error, T = any>(syncFunction: () => T): Result<E, T>
export function attempt<E extends Error, T = any>(asyncFunctionOrPromise: Promise<T> | (() => T | Promise<T>)): Attempt<E, T> | Result<E, T> {
  try {
    const result = isPromise(asyncFunctionOrPromise) ? asyncFunctionOrPromise : asyncFunctionOrPromise()
    return isPromise(result)
      ? result.then(success).catch<Fail<E>>(failure)
      : success(result)
  }
  catch (e: any) {
    return failure<E>(e)
  }
}

export const isFunction = <T>(fn: any): fn is (() => Promise<T> | T) => typeof fn === 'function'
export const isPromise = (obj: any): obj is Promise<unknown> => !!obj && typeof obj.then === 'function'
export const failure = <E extends Error>(err: any): Fail<E> => [err, undefined] as Fail<E>
export const success = <T>(val: T): Success<T> => [undefined, val] as Success<T>

type PromiseFallback<T> = Promise<T> | (() => T | Promise<T>) | Exclude<T, Promise<unknown> | ((...args: any[]) => any)>
type Fallback<T> = T | (() => T)

export async function attemptWithFallback<T>(asyncFunctionOrPromise: Promise<T> | (() => Promise<T>), fallback: PromiseFallback<T>): Promise<T>
export function attemptWithFallback<T>(syncFunction: () => T, fallback: Fallback<T>): T
export function attemptWithFallback<T>(
  asyncFunctionOrPromise: Promise<T> | (() => Promise<T>) | (() => T),
  fallback: PromiseFallback<T> | Fallback<T>,
): Promise<T> | T {
  const getFallback = () => isFunction<T>(fallback) ? fallback() : fallback
  try {
    const result = isPromise(asyncFunctionOrPromise) ? asyncFunctionOrPromise : asyncFunctionOrPromise()
    return isPromise(result) ? result.catch(getFallback) : result
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  catch (err: any) {
    return getFallback()
  }
}
